import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, X } from "@phosphor-icons/react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/context/AuthContext";
import Pesquisa from "@/components/eng/Pesquisa";
import Calculadora from "@/components/eng/Calculadora";
import TabelasAdmin from "@/components/eng/TabelasAdmin";
import Manutencoes from "@/components/eng/Manutencoes";
import { errMsg, fmtBRL, fmtDateTime } from "@/lib/medicoes";

export const useEngBase = () => (useLocation().pathname.startsWith("/fornecedor") ? "/fornecedor/engenharia" : "/engenharia");

function NovoOrcamento({ onClose }) {
  const nav = useNavigate();
  const base = useEngBase();
  const { user } = useAuth();
  const [tabelas, setTabelas] = useState([]);
  const [contratos, setContratos] = useState([]);
  const [f, setF] = useState({ titulo: "", obra: "", endereco: "", tabela_id: "", contract_id: "", responsavel_tecnico: "", registro_profissional: "", municipio_id: "" });
  useEffect(() => {
    api.get("/engenharia/tabelas").then((r) => { const p = r.data.items.filter((t) => t.status === "publicada"); setTabelas(p); if (p[0]) setF((s) => ({ ...s, tabela_id: p[0].id })); });
    if (user?.role === "fornecedor") api.get("/fornecedor/contratos").then((r) => setContratos(r.data.items)).catch(() => {});
  }, [user]);
  const salvar = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/engenharia/orcamentos", { ...f, contract_id: f.contract_id || null, municipio_id: f.municipio_id || null });
      toast.success(`Orçamento ${data.numero} criado`);
      nav(`${base}/orcamentos/${data.id}`);
    } catch (err) { toast.error(errMsg(err)); }
  };
  const inp = "tce-input w-full px-3 py-2.5 rounded-md text-sm";
  return (
    <div className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={salvar} onClick={(e) => e.stopPropagation()} className="tce-card w-full max-w-2xl p-6 space-y-3" data-testid="novo-orcamento-modal">
        <div className="flex justify-between"><div className="font-heading text-xl">Novo orçamento de obra</div><button type="button" onClick={onClose}><X size={18} /></button></div>
        {!tabelas.length && <p className="text-sm text-[#F59E0B]">Nenhuma tabela oficial publicada ainda.</p>}
        <input data-testid="no-titulo" required minLength={3} value={f.titulo} onChange={(e) => setF({ ...f, titulo: e.target.value })} placeholder="Título do orçamento*" className={inp} />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <input data-testid="no-obra" value={f.obra} onChange={(e) => setF({ ...f, obra: e.target.value })} placeholder="Obra / projeto" className={inp} />
          <input value={f.endereco} onChange={(e) => setF({ ...f, endereco: e.target.value })} placeholder="Endereço / localização" className={inp} />
          <input value={f.responsavel_tecnico} onChange={(e) => setF({ ...f, responsavel_tecnico: e.target.value })} placeholder="Responsável técnico" className={inp} />
          <input value={f.registro_profissional} onChange={(e) => setF({ ...f, registro_profissional: e.target.value })} placeholder="CREA/CAU" className={inp} />
        </div>
        <select data-testid="no-tabela" required value={f.tabela_id} onChange={(e) => setF({ ...f, tabela_id: e.target.value })} className={inp}>
          {tabelas.map((t) => <option key={t.id} value={t.id}>{`${t.fonte} · ${t.competencia || "s/ competência"} · ${t.regime}${t.versao ? ` · ${t.versao}` : ""}`}</option>)}
        </select>
        {user?.role === "fornecedor" ? (
          <select data-testid="no-contrato" value={f.contract_id} onChange={(e) => setF({ ...f, contract_id: e.target.value })} className={inp}>
            <option value="">Sem contrato (obra/projeto avulso)</option>{contratos.map((c) => <option key={c.id} value={c.id}>{`${c.numero} — ${c.objeto?.slice(0, 60) || ""}`}</option>)}
          </select>
        ) : <input data-testid="no-contrato-id" value={f.contract_id} onChange={(e) => setF({ ...f, contract_id: e.target.value })} placeholder="ID do contrato (opcional)" className={inp} />}
        {user?.role === "admin" && <input value={f.municipio_id} onChange={(e) => setF({ ...f, municipio_id: e.target.value })} placeholder="Código IBGE do município (opcional)" className={inp} />}
        <button data-testid="no-salvar" disabled={!tabelas.length} className="w-full py-2.5 rounded-md bg-[#FFDF00] text-[#0F172A] font-medium text-sm disabled:opacity-40">Criar orçamento</button>
      </form>
    </div>
  );
}

function Orcamentos() {
  const base = useEngBase();
  const [items, setItems] = useState(null);
  const [novo, setNovo] = useState(false);
  const [q, setQ] = useState("");
  useEffect(() => { const t = setTimeout(() => api.get("/engenharia/orcamentos", { params: { q } }).then((r) => setItems(r.data.items)), 250); return () => clearTimeout(t); }, [q]);
  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        <input data-testid="orc-busca" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar orçamento…" className="tce-input flex-1 px-3 py-2.5 rounded-md text-sm" />
        <button data-testid="novo-orcamento-btn" onClick={() => setNovo(true)} className="px-4 py-2.5 rounded-md bg-[#FFDF00] text-[#0F172A] text-sm font-medium flex items-center gap-2"><Plus size={16} /> Novo orçamento</button>
      </div>
      <div className="tce-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#1E293B] text-left"><tr>{["Número", "Título / obra", "Referência", "Contrato", "Itens", "Situação", "Atualizado"].map((h) => <th key={h} className="px-4 py-3 uppercase-label !text-[10px]">{h}</th>)}</tr></thead>
          <tbody>
            {items?.map((o, i) => (
              <tr key={o.id} className="border-t border-[#334155]" data-testid={`orc-row-${i}`}>
                <td className="px-4 py-3"><Link to={`${base}/orcamentos/${o.id}`} className="font-mono text-[#FFDF00] hover:underline" data-testid={`orc-link-${i}`}>{o.numero} R{o.revisao}</Link></td>
                <td className="px-4 py-3">{o.titulo}<div className="text-[10px] text-[#94A3B8]">{o.obra}</div></td>
                <td className="px-4 py-3 text-xs text-[#94A3B8]">{o.tabela?.fonte} {o.tabela?.competencia}</td>
                <td className="px-4 py-3 text-xs">{o.contract_numero || "—"}</td>
                <td className="px-4 py-3 text-xs">{o.congelado ? fmtBRL(o.congelado.calculo.total) : "—"}</td>
                <td className="px-4 py-3 text-xs">{o.status}</td>
                <td className="px-4 py-3 text-xs text-[#94A3B8]">{fmtDateTime(o.updated_at)}</td>
              </tr>
            ))}
            {items && !items.length && <tr><td colSpan={7} className="px-4 py-8 text-center text-[#94A3B8]">Nenhum orçamento.</td></tr>}
          </tbody>
        </table>
      </div>
      {novo && <NovoOrcamento onClose={() => setNovo(false)} />}
    </div>
  );
}

export default function Engenharia() {
  const { user } = useAuth();
  return (
    <div className="space-y-6" data-testid="engenharia-page">
      <div>
        <div className="uppercase-label">Engenharia e Orçamentos · SEINFRA-CE e SINAPI-CE</div>
        <h1 className="font-heading text-4xl tracking-tight mt-2">Engenharia e Orçamentos</h1>
        <p className="text-sm text-[#94A3B8] mt-2 max-w-3xl">Tabelas oficiais versionadas, composições analíticas, calculadora de quantitativos com memória de cálculo, orçamentos com BDI e relatórios. O sistema apoia os cálculos; a responsabilidade técnica (ART/RRT) é do profissional habilitado.</p>
      </div>
      <Tabs defaultValue="orcamentos">
        <TabsList className="bg-[#1E293B] flex-wrap h-auto">
          <TabsTrigger value="orcamentos" data-testid="eng-tab-orcamentos">Orçamentos</TabsTrigger>
          <TabsTrigger value="pesquisa" data-testid="eng-tab-pesquisa">Pesquisa de composições</TabsTrigger>
          <TabsTrigger value="calculadora" data-testid="eng-tab-calculadora">Calculadora de quantitativos</TabsTrigger>
          <TabsTrigger value="manutencoes" data-testid="eng-tab-manutencoes">O que mudou na SINAPI</TabsTrigger>
          {user?.role === "admin" && <TabsTrigger value="tabelas" data-testid="eng-tab-tabelas">Tabelas oficiais</TabsTrigger>}
        </TabsList>
        <TabsContent value="orcamentos" className="mt-4"><Orcamentos /></TabsContent>
        <TabsContent value="pesquisa" className="tce-card p-5 mt-4"><Pesquisa /></TabsContent>
        <TabsContent value="calculadora" className="tce-card p-5 mt-4"><Calculadora /></TabsContent>
        <TabsContent value="manutencoes" className="tce-card p-5 mt-4"><Manutencoes /></TabsContent>
        {user?.role === "admin" && <TabsContent value="tabelas" className="mt-4"><TabelasAdmin /></TabsContent>}
      </Tabs>
    </div>
  );
}
